// Helper functions for evaluation metrics

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
};

export const getStatusBadgeClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-success';
    case 'failed':
      return 'bg-danger';
    case 'running':
    case 'processing':
      return 'bg-warning';
    case 'pending':
      return 'bg-secondary';
    default:
      return 'bg-info';
  }
};

// Transform raw database results to the format expected by evaluation API
export const transformVerificationResults = (rawResults) => {
  if (!rawResults) {
    return {
      total_publications: 0,
      found_in_database: 0,
      not_found: 0,
      detailed_results: []
    };
  }

  // Handle case where rawResults might be wrapped in an object
  let resultsArray = rawResults;
  if (!Array.isArray(rawResults)) {
    // Check if results are in a nested property
    if (rawResults.results && Array.isArray(rawResults.results)) {
      resultsArray = rawResults.results;
    } else if (rawResults.verification_results && Array.isArray(rawResults.verification_results)) {
      resultsArray = rawResults.verification_results;
    } else {
      return {
        total_publications: 0,
        found_in_database: 0,
        not_found: 0,
        detailed_results: []
      };
    }
  }

  if (resultsArray.length === 0) {
    return {
      total_publications: 0,
      found_in_database: 0,
      not_found: 0,
      detailed_results: []
    };
  }

  // Check if data is already in literature format (each record is a literature item)
  // vs verification format (each record is a verification check per database per literature)
  const firstRecord = resultsArray[0];
  const isLiteratureFormat = firstRecord && (
    firstRecord.execution_id !== undefined || 
    !firstRecord.database_name
  );

  let detailedResults;
  let foundCount;

  if (isLiteratureFormat) {
    detailedResults = resultsArray.map(result => ({
      literature_id: result.id || result.literature_id,
      title: result.title || '',
      authors: result.authors || '',
      year: result.year,
      doi: result.doi || '',
      found_in_database: true, // If it's in the database, it was verified/found
      databases_checked: [],
      best_match_similarity: 1.0
    }));
    foundCount = detailedResults.length; // All publications in this format are found
  } else {
    const publicationMap = {};
    resultsArray.forEach((result, index) => {
      // Handle different possible field names for literature ID
      const litId = result.literature_id || result.literatureId || result.publication_id || result.id;
      
      if (!litId) return;

      if (!publicationMap[litId]) {
        publicationMap[litId] = {
          literature_id: litId,
          title: result.title || result.publication_title || '',
          authors: result.authors || result.author || '',
          year: result.year || result.publication_year,
          doi: result.doi || '',
          found_in_database: false,
          databases_checked: [],
          best_match_similarity: 0
        };
      }
      
      // Track which databases were checked
      const dbName = result.database_name || result.database || 'Unknown';
      if (!publicationMap[litId].databases_checked.includes(dbName)) {
        publicationMap[litId].databases_checked.push(dbName);
      }
      
      // Check if this publication was found in the database
      // Handle different possible field names for "found" status
      const isFound = result.found === true || 
                     result.found === 1 || 
                     result.verified === true || 
                     result.is_verified === true ||
                     result.match_found === true;
      
      if (isFound) {
        publicationMap[litId].found_in_database = true;
        const similarity = result.similarity_score || result.similarity || result.score || 0;
        if (similarity > publicationMap[litId].best_match_similarity) {
          publicationMap[litId].best_match_similarity = similarity;
        }
      }
    });

    detailedResults = Object.values(publicationMap);
    foundCount = detailedResults.filter(pub => pub.found_in_database).length;
  }

  return {
    total_publications: detailedResults.length,
    found_in_database: foundCount,
    not_found: detailedResults.length - foundCount,
    detailed_results: detailedResults
  };
};

export const transformComparisonResults = (rawResults) => {
  if (!rawResults) {
    return {
      total_comparisons: 0,
      exact_matches: 0,
      partial_matches: 0,
      no_matches: 0,
      detailed_results: []
    };
  }

  // ComparisonResultsEnvelope (workflow + import GET /comparison-results)
  if (Array.isArray(rawResults.detailed_results)) {
    const summary = rawResults.summary && typeof rawResults.summary === 'object' ? rawResults.summary : {};
    return {
      total_comparisons:
        rawResults.total_ground_truth ??
        summary.total_gt_papers ??
        rawResults.detailed_results.length,
      exact_matches: rawResults.exact_matches ?? summary.exact_count ?? 0,
      partial_matches: rawResults.partial_matches ?? summary.partial_count ?? 0,
      no_matches:
        rawResults.no_matches ??
        summary.no_match_count ??
        Math.max(
          0,
          (rawResults.detailed_results.length || 0) -
            (rawResults.exact_matches ?? summary.exact_count ?? 0) -
            (rawResults.partial_matches ?? summary.partial_count ?? 0)
        ),
      detailed_results: rawResults.detailed_results,
      summary: rawResults.summary ?? summary,
    };
  }

  // Handle case where rawResults might be wrapped in an object
  let resultsArray = rawResults;
  if (!Array.isArray(rawResults)) {
    // Check if results are in a nested property
    if (rawResults.results && Array.isArray(rawResults.results)) {
      resultsArray = rawResults.results;
    } else if (rawResults.comparison_results && Array.isArray(rawResults.comparison_results)) {
      resultsArray = rawResults.comparison_results;
    } else {
      return {
        total_comparisons: 0,
        exact_matches: 0,
        partial_matches: 0,
        no_matches: 0,
        detailed_results: []
      };
    }
  }

  if (resultsArray.length === 0) {
    return {
      total_comparisons: 0,
      exact_matches: 0,
      partial_matches: 0,
      no_matches: 0,
      detailed_results: []
    };
  }

  const exactMatches = resultsArray.filter(r => 
    r.match_status === 'exact' || r.match_status === 'EXACT' || r.status === 'exact' ||
    r.is_exact_match === true
  ).length;
  const partialMatches = resultsArray.filter(r => 
    r.match_status === 'partial' || r.match_status === 'PARTIAL' || r.status === 'partial' ||
    r.is_partial_match === true
  ).length;

  return {
    total_comparisons: resultsArray.length,
    exact_matches: exactMatches,
    partial_matches: partialMatches,
    no_matches: resultsArray.length - exactMatches - partialMatches,
    detailed_results: resultsArray
  };
};

// Calculate relevance metrics according to EVALUATION_METRICS_GUIDE.md
export const calculateRelevanceMetrics = (execution, comparisonResults, groundTruthCount = 0) => {
  const missingData = [];
  
  // Get total publications generated by LLM
  const totalLLMPublications = execution.total_publications_found;
  if (totalLLMPublications === undefined || totalLLMPublications === null) {
    missingData.push('total_publications_found from execution');
  }
  
  // Get comparison data
  if (!comparisonResults || !comparisonResults.detailed_results) {
    missingData.push('comparison_results.detailed_results');
    return {
      hasRequiredData: false,
      missingData,
      metrics: {}
    };
  }
  
  // Calculate True Positives (publications that match ground truth)
  // From guide: True Positives = Publications that match ground truth
  // This includes both exact and partial matches
  const exactMatches = comparisonResults.exact_matches || 0;
  const partialMatches = comparisonResults.partial_matches || 0;
  const truePositives = exactMatches + partialMatches;
  
  // Calculate False Positives (LLM publications that don't match ground truth)
  // From guide: False Positives = Publications that don't match ground truth
  const falsePositives = totalLLMPublications - truePositives;
  
  // Get total ground truth count
  // Priority: 1. Passed parameter, 2. Comparison results, 3. Execution object
  let totalGroundTruth = groundTruthCount;
  
  if (totalGroundTruth === 0) {
    // Try from comparison results
    totalGroundTruth = comparisonResults.total_comparisons || 0;
  }
  
  if (totalGroundTruth === 0) {
    // Try from execution object
    if (execution.ground_truth_count !== undefined) {
      totalGroundTruth = execution.ground_truth_count;
    } else if (execution.seed_paper?.ground_truth_count !== undefined) {
      totalGroundTruth = execution.seed_paper.ground_truth_count;
    }
  }
  
  // If still zero, mark as missing
  if (totalGroundTruth === 0) {
    missingData.push('total_ground_truth (no ground truth references found for seed paper)');
  }
  
  // Calculate False Negatives (ground truth publications LLM missed)
  // From guide: False Negatives = Ground truth publications the LLM missed
  const falseNegatives = totalGroundTruth - truePositives;
  
  // Calculate Precision = TP / (TP + FP)
  // From guide: "When the LLM suggests a paper, how likely is it to be relevant?"
  const precision = totalLLMPublications > 0 
    ? truePositives / totalLLMPublications 
    : 0;
  
  // Calculate Recall = TP / (TP + FN)
  // From guide: "Of all relevant papers, what % did the LLM find?"
  const recall = totalGroundTruth > 0 
    ? truePositives / totalGroundTruth 
    : 0;
  
  // Calculate F1-Score = 2 × (Precision × Recall) / (Precision + Recall)
  // From guide: Harmonic mean balancing precision and recall
  const f1Score = (precision + recall) > 0 
    ? (2 * precision * recall) / (precision + recall) 
    : 0;
  
  // Check if we have all required data for a complete calculation
  const hasRequiredData = missingData.length === 0 && totalGroundTruth > 0;
  
  if (!hasRequiredData) {
    return {
      hasRequiredData: false,
      missingData: missingData.length > 0 ? missingData : ['total_ground_truth is 0'],
      metrics: {
        true_positives: truePositives,
        false_positives: Math.max(0, falsePositives),
        false_negatives: falseNegatives >= 0 ? falseNegatives : 'unknown',
        total_ground_truth: totalGroundTruth,
        precision,
        recall,
        f1_score: f1Score
      }
    };
  }
  
  return {
    hasRequiredData: true,
    missingData: [],
    metrics: {
      true_positives: truePositives,
      false_positives: Math.max(0, falsePositives),
      false_negatives: Math.max(0, falseNegatives),
      total_ground_truth: totalGroundTruth,
      precision,
      recall,
      f1_score: f1Score
    }
  };
};

/**
 * Weighted Matthews Correlation Coefficient (WMCC).
 *
 * Paper: LLM4SCREENLIT (Weighted MCC), with weighting factor `w` applied to positive examples (TP, FN).
 *
 * WMCC = (w*TP*TN - FP*w*FN) / sqrt( (w*TP+FP)*(w*TP+w*FN)*(TN+FP)*(TN+w*FN) )
 */
export const calculateWMCC = ({ tp, fp, fn, tn, w = 10 }) => {
  const toNumber = (v) => (typeof v === 'number' ? v : Number(v));
  const TP = toNumber(tp);
  const FP = toNumber(fp);
  const FN = toNumber(fn);
  const TN = toNumber(tn);
  const W = toNumber(w);

  const allFinite =
    Number.isFinite(TP) &&
    Number.isFinite(FP) &&
    Number.isFinite(FN) &&
    Number.isFinite(TN) &&
    Number.isFinite(W);

  if (!allFinite) return undefined;

  const a = W * TP + FP;
  const b = W * TP + W * FN;
  const c = TN + FP;
  const d = TN + W * FN;

  const denom = Math.sqrt(a * b * c * d);
  if (!Number.isFinite(denom) || denom === 0) return undefined;

  return (W * TP * TN - FP * W * FN) / denom;
};





